import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Mail, Send } from "lucide-react";
import StaffShell from "../components/staff/StaffShell";
import { CommissionPanel } from "../components/commission/CommissionPanel";
import { StaffDeadlinePanel } from "../components/commission/StaffDeadlinePanel";
import { StaffCountersignPanel } from "../components/commission/StaffCountersignPanel";
import { CommissionButton } from "../components/commission/CommissionButton";

const API = process.env.REACT_APP_BACKEND_URL;

// Fictional project used for the design preview. Nothing here touches
// Cognito, SharePoint, email or the database.
const SAMPLE_D = {
  proposal_number: 7301,
  project_reference: "Oncologist perceptions of first line treatment sequencing in metastatic NSCLC",
  client_company: "Northwind Health Insights",
  prepared_by: "Frank Desbuquois",
  proposal_date: "2026-09-21",
  sub_total: 48750,
};

const PREFILL = {
  signatory_name: "",
  signatory_email: "",
  invoice_company: "Northwind Health Insights Ltd",
  invoice_email: "accounts@northwindhealth.com",
  invoice_address: { line1: "12 Harbour Quay", city: "Bristol", postal_code: "BS1 5UH", country: "United Kingdom" },
  vat: "Yes",
  project_deadline: "2027-01-29",
  update_frequency: ["Tuesday", "Thursday"],
  uses_po: "yes",
};

const BASE = {
  stage: "open",
  stage_label: "Not started",
  can_write: true,
  closed: false,
  expired: false,
  valid_until: "2026-12-20",
  commissioned: false,
  route: null,
  totals: { net_total: 48750, payment_terms: "30 days from invoice" },
  client_company: "Northwind Health Insights",
  client_contact: "Dr Amelia Hart",
  client_code: "412A",
  master_project_number: "412A267301",
  client_record: {
    code: "412A",
    company: "Northwind Health Insights Ltd",
    short_name: "Northwind",
    invoice_email: "accounts@northwindhealth.com",
    vat: "Yes",
    use_po: "Yes",
    terms: "30 days",
    address: { line1: "12 Harbour Quay", line2: null, city: "Bristol", postal_code: "BS1 5UH", country: "United Kingdom" },
  },
  bd_name: "Frank Desbuquois",
  deadline: { deadline: null, quoted_working_days: 45, available_working_days: null, short: false, confirmed: false, requested_at: null },
  prefill: PREFILL,
  answers: {},
  signature: null,
  executed_pdf: false,
  client_paper: { drafts: [], submitted_at: null, will_follow_by_email: false },
  error: null,
  busy: false,
};

const FILLED = {
  ...PREFILL,
  po_number: "PO-48213",
  vat: "Yes",
  invoice_email: "accounts@northwindhealth.com",
  invoice_address: { line1: "12 Harbour Quay", city: "Bristol", postal_code: "BS1 5UH", country: "United Kingdom" },
  updates_emails: "procurement@northwindhealth.com",
  client_pm: "James Okafor",
  client_pm_email: "j.okafor@northwindhealth.com",
  client_pm_phone: "+44 117 496 0123",
};

const SIGNED = {
  stage: "signed",
  commissioned: true,
  route: "medicys_sow",
  answers: FILLED,
  executed_pdf: true,
  signature: { name: "Dr Amelia Hart", email: "amelia.hart@northwindhealth.com", signed_at: new Date().toISOString() },
};

const V1_HISTORY = [{ index: 0, version: 1, document_id: "SOW-7301-20261006-SAMPLE", signed_at: "2026-10-06T10:25:11Z", signed_by: "Dr Amelia Hart",
  countersigned_at: "2026-10-06T15:02:40Z", superseded_at: "2026-10-14T09:12:00Z", downloadable: true }];
const AMENDMENT = { version: 2, issued_at: "2026-10-14T09:12:00Z", issued_by_name: "Frank Desbuquois",
  summary: "The sample has grown from 30 to 36 oncologists at your request, which adds £9,250 to the net value. The project deadline moves to 12 March 2027 to allow for the additional recruitment.",
  supersedes: { document_id: "SOW-7301-20261006-SAMPLE", signed_at: "2026-10-06T10:25:11Z", signed_by: "Dr Amelia Hart" } };

const SCENARIOS = [
  ["open", "1. Confirm", {}],
  ["open_expired", "1b. Confirm, quote expired", { expired: true, valid_until: "2026-06-30" }],
  ["expired_request", "1c. Expired, request recorded", { stage: "expired_request", expired: true, valid_until: "2026-06-30" }],
  ["costs_refreshed", "1d. Costs reconfirmed", { stage: "open", expired: false, costs_refreshed: true, valid_until: "2027-01-11" }],
  ["commissioned", "2. Choose a route", { stage: "commissioned", commissioned: true }],
  ["medicys_sow", "3a. Medicys SOW form", { stage: "medicys_sow", commissioned: true, route: "medicys_sow", answers: FILLED }],
  ["sign_failed", "3a. Signing failed", { stage: "sign_failed", commissioned: true, route: "medicys_sow", answers: FILLED }],
  ["signing", "4a. Processing the signature", { stage: "signing", commissioned: true, route: "medicys_sow", answers: FILLED }],
  ["signed", "5a. Signed", SIGNED],
  ["amendment", "3c. Amended SOW, version 2 to sign", { stage: "medicys_sow", commissioned: true, signed: true, route: "medicys_sow", answers: FILLED, version: 2, amendment: AMENDMENT, versions: V1_HISTORY }],
  ["returned", "3d. Returned for a change before countersigning", { stage: "medicys_sow", commissioned: true, route: "medicys_sow", answers: FILLED,
    returned: { reason: "The purchase order number entered (PO-48213) belongs to last year's project; please enter the PO raised for this study.", at: "2026-10-07T08:40:00Z", by_name: "Frank Desbuquois", signed_at: "2026-10-06T10:25:11Z" } }],
  ["signed_v2", "5c. Signed, version 2", { ...SIGNED, version: 2, amended: { version: 2, issued_at: "2026-10-14T09:12:00Z" }, versions: V1_HISTORY,
    countersignature: { name: "Frank Desbuquois", signed_at: new Date().toISOString() } }],
  ["client_paper", "3b. Client paper form", { stage: "client_paper", commissioned: true, route: "client_paper", answers: FILLED }],
  ["client_paper_submitting", "4b. Sending contract details", { stage: "client_paper_submitting", commissioned: true, route: "client_paper", answers: FILLED }],
  ["client_paper_submitted", "5b. Contract details sent", { stage: "client_paper_submitted", commissioned: true, route: "client_paper", answers: FILLED, client_paper: { drafts: [{ id: "d1", filename: "Northwind MSA v3 draft.docx", size_bytes: 184320 }], submitted_at: new Date().toISOString(), will_follow_by_email: false } }],
  ["readonly", "Staff read only view", { can_write: false }],
  ["staff_deadline", "Staff: deadline decision", { can_write: false, staff_deadline: true }],
  ["staff_countersign", "Staff: countersign or return", { ...SIGNED, stage: "client_signed", executed_pdf: false, can_write: false, staff_review: "awaiting" }],
  ["staff_amend", "Staff: executed, amend and re-issue", { ...SIGNED, can_write: false, staff_review: "signed" }],
  ["staff_amend_pending", "Staff: amendment with the client", { stage: "medicys_sow", commissioned: true, signed: true, route: "medicys_sow", answers: FILLED, version: 2,
    amendment: AMENDMENT, versions: V1_HISTORY, can_write: false, staff_review: "pending" }],
];

const STAFF_FLOW = { stage: "signed", route: "medicys_sow", signed_at: "2026-10-06T10:25:11Z", signed_by: "Dr Amelia Hart", signer_email: "amelia.hart@northwindhealth.com",
  countersigned_at: "2026-10-06T15:02:40Z", countersigned_by: "Frank Desbuquois", document_id: "SOW-7301-20261006-SAMPLE", version: 1, amendment: null, returned: null, versions: [],
  client_signed_doc_id: "sample", executed_doc_id: "sample", awaiting_countersign: false };
const STAFF_REVIEW = {
  awaiting: { cid: 7301, client_company: "Northwind Health Insights", bd_email: "f.desbuquois@medicysltd.co.uk", bd_poc: "Frank Desbuquois", awaiting: true, countersigning: false,
    signed: false, executed_doc_id: null, amendable: false, amendment_pending: false,
    client_flow: { ...STAFF_FLOW, stage: "client_signed", countersigned_at: null, countersigned_by: null, executed_doc_id: null, awaiting_countersign: true } },
  signed: { cid: 7301, client_company: "Northwind Health Insights", bd_email: "f.desbuquois@medicysltd.co.uk", bd_poc: "Frank Desbuquois", awaiting: false, countersigning: false,
    signed: true, executed_doc_id: "sample", amendable: true, amendment_pending: false, client_flow: STAFF_FLOW },
  pending: { cid: 7301, client_company: "Northwind Health Insights", bd_email: "f.desbuquois@medicysltd.co.uk", bd_poc: "Frank Desbuquois", awaiting: false, countersigning: false,
    signed: false, executed_doc_id: null, amendable: false, amendment_pending: true,
    client_flow: { ...STAFF_FLOW, stage: "medicys_sow", version: 2, amendment: AMENDMENT, versions: V1_HISTORY, signed_at: null, signed_by: null } },
};

const DEADLINE_REVIEW = {
  check: { deadline: "2026-10-30", quoted_working_days: 45, available_working_days: 16, requested_at: new Date().toISOString(), requested_by: "amelia.hart@northwindhealth.com" },
  current: { deadline: "2026-10-30", quoted_working_days: 45, available_working_days: 16, short: true, confirmed: false },
  client_name: "Dr Amelia Hart",
  client_email: "amelia.hart@northwindhealth.com",
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// Local stand in for useCommission: the same surface, state kept in
// memory, with short delays so the processing screens can be seen.
function useMockCommission(initial) {
  const [state, setState] = useState(initial);
  const patch = (p) => setState((s) => ({ ...s, ...(typeof p === "function" ? p(s) : p) }));
  return {
    state,
    error: null,
    reload: async () => state,
    start: async () => {
      await wait(400);
      patch((s) => (s.expired ? { stage: "expired_request" } : { stage: "commissioned", commissioned: true }));
    },
    chooseRoute: async (route) => { await wait(300); patch({ stage: route, route }); },
    saveAnswers: async (a) => {
      await wait(250);
      patch((s) => {
        const answers = { ...s.answers, ...a };
        const d = answers.project_deadline;
        const available = d ? Math.max(0, Math.round((new Date(d) - Date.now()) / 86400000 * 5 / 7)) : null;
        const keep = s.deadline?.requested_at && s.deadline.deadline === d;
        return { answers, deadline: { ...s.deadline, deadline: d || null, available_working_days: available, short: !!d && available < 45, confirmed: !!keep && !!s.deadline.confirmed, requested_at: keep ? s.deadline.requested_at : null } };
      });
    },
    requestDeadlineCheck: async () => { await wait(400); patch((s) => ({ deadline: { ...s.deadline, requested_at: new Date().toISOString() } })); },
    sign: async (body) => {
      const a = { ...(body.answers || {}), signatory_name: body.signatory_name || body.answers?.signatory_name };
      const need = { signatory_name: "Your name", client_reference: "Your internal reference for this project", project_deadline: "Project deadline", client_pm: "Project main point of contact", client_pm_email: "Email address of your main point of contact" };
      const missing = Object.entries(need).filter(([k]) => !a[k]);
      if ((a.signatory_name || "").trim().split(/\s+/).filter(Boolean).length < 2) missing.push(["signatory_name", "Your first name and surname"]);
      if (missing.length) {
        const err = new Error("incomplete");
        err.response = { data: { detail: { reason: "incomplete", missing: missing.map(([, l]) => l), fields: missing.map(([k]) => k) } } };
        throw err;
      }
      patch({ stage: "signing", answers: { ...a, effective_from: new Date().toISOString().slice(0, 10) } });
      await wait(2500);
      patch({ stage: "signed", executed_pdf: true, signature: { name: body.signatory_name, email: body.answers.signatory_email, signed_at: new Date().toISOString() } });
    },
    submitClientPaper: async (answers) => {
      if (!(state.client_paper?.drafts || []).length && !answers.will_follow_by_email) {
        const err = new Error("incomplete");
        err.response = { data: { detail: { reason: "incomplete", missing: ["Upload the draft or tick that it will follow by email"], fields: ["draft"] } } };
        throw err;
      }
      patch({ stage: "client_paper_submitting", answers });
      await wait(1800);
      patch({ stage: "client_paper_submitted", client_paper: { ...(state.client_paper || {}), submitted_at: new Date().toISOString(), will_follow_by_email: !!answers.will_follow_by_email } });
    },
    uploadDraft: async (file) => {
      await wait(700);
      patch((s) => ({ client_paper: { ...(s.client_paper || {}), drafts: [...(s.client_paper?.drafts || []), { id: `d${Date.now()}`, filename: file.name, size_bytes: file.size }] } }));
    },
    removeDraft: async (id) => patch((s) => ({ client_paper: { ...(s.client_paper || {}), drafts: (s.client_paper?.drafts || []).filter((x) => x.id !== id) } })),
    previewUrl: `${API}/api/sow/preview/sample.pdf?variant=${state.amendment ? "amended" : "draft"}`,
    executedUrl: `${API}/api/sow/preview/sample.pdf?variant=executed`,
    versionUrl: () => `${API}/api/sow/preview/sample.pdf?variant=executed`,
  };
}

function Scenario({ scenario }) {
  const initial = useMemo(() => ({ ...BASE, ...scenario[2] }), [scenario]);
  const commission = useMockCommission(initial);
  const [tabOpen, setTabOpen] = useState(initial.stage !== "open");
  return (
    <div className="rounded-2xl overflow-hidden border border-stone-200 shadow-[0_30px_80px_-40px_rgba(11,18,32,0.45)]" data-testid="sow-preview-frame">
      {initial.staff_review && (
        <div className="bg-stone-100 pb-4">
          <StaffCountersignPanel token="sample" initial={STAFF_REVIEW[initial.staff_review]} />
        </div>
      )}
      {initial.staff_deadline && (
        <div className="bg-stone-100 pb-4">
          <StaffDeadlinePanel
            token="sample"
            initial={DEADLINE_REVIEW}
            onDecide={async (body) => {
              await wait(300);
              const check = { ...DEADLINE_REVIEW.check, decision: body.decision, decided_at: new Date().toISOString(), decided_by: "f.desbuquois@medicysltd.co.uk", workable_from: body.workable_from, note: body.note };
              return { ...DEADLINE_REVIEW, check, current: { ...DEADLINE_REVIEW.current, confirmed: body.decision === "approved" } };
            }}
          />
        </div>
      )}
      <div className="bg-[#0B1220] text-stone-100 px-6 sm:px-10 py-8">
        <p className="text-xs tracking-[0.25em] uppercase text-[#3EA8DC] font-medium">Proposal #{SAMPLE_D.proposal_number}</p>
        <h1 className="font-serif font-light text-2xl md:text-3xl text-white mt-2 max-w-3xl leading-tight">{SAMPLE_D.project_reference}</h1>
        <p className="text-sm text-stone-300 mt-3">Prepared for · {SAMPLE_D.client_company} · {initial.client_contact}</p>
        <div className="mt-7" data-testid="hero-action">
          <CommissionButton commission={commission} d={SAMPLE_D} onOpenTab={() => setTabOpen(true)} active={tabOpen} variant="hero" />
        </div>
      </div>
      <div className="bg-stone-50/95 border-b border-stone-200 px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between gap-4" data-testid="sow-preview-tabbar">
        <div className="inline-flex gap-1 rounded-lg bg-stone-200/60 p-1 text-sm font-semibold text-stone-500">
          {["Specifications", "Assumptions", "Costs", "Timelines", "Terms"].map((t, i) => (
            <button key={t} type="button" onClick={() => setTabOpen(false)} className={`px-3.5 py-2.5 rounded-md whitespace-nowrap ${!tabOpen && i === 0 ? "bg-white text-stone-900 shadow-sm" : ""}`}>{t}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 shrink-0 whitespace-nowrap">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#0d9488] text-white px-4 py-2 text-[11px] font-mono font-semibold tracking-[0.14em] uppercase opacity-80">
            <Send className="w-3.5 h-3.5" /> Forward to a colleague
          </span>
        </div>
      </div>
      <div className="bg-stone-50 px-4 sm:px-6 lg:px-10 py-8 sm:py-10">
        {tabOpen ? (
          <CommissionPanel d={SAMPLE_D} commission={commission} />
        ) : (
          <p className="text-sm text-stone-500 text-center py-10" data-testid="sow-preview-tab-hint">The proposal content sits here. Use the Commission button above to start; once started, the same button brings the client back to the paperwork.</p>
        )}
      </div>
    </div>
  );
}

export default function SowPreview() {
  const [key, setKey] = useState("open");
  const [epoch, setEpoch] = useState(0);
  const scenario = SCENARIOS.find((s) => s[0] === key) || SCENARIOS[0];
  return (
    <StaffShell max="wide">
      <div className="flex flex-col gap-6" data-testid="sow-preview-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Link to="/sow/emails" className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-800" data-testid="sow-preview-back">
              <ArrowLeft className="w-3.5 h-3.5" /> Commissioning emails
            </Link>
            <h1 className="text-2xl font-semibold text-[#1B4F8A] tracking-tight mt-2">Client flow design preview</h1>
            <p className="text-sm text-stone-500 mt-1 max-w-2xl">
              Every screen a client sees when commissioning from the proposal viewer, rendered with a fictional project.
              Clicking through works, but nothing is written anywhere and no emails are sent.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[
              ["sow-preview-pdf-draft", `${API}/api/sow/preview/sample.pdf?variant=draft`, "Sample draft PDF", FileText],
              ["sow-preview-pdf-executed", `${API}/api/sow/preview/sample.pdf?variant=executed`, "Sample signed PDF", FileText],
              ["sow-preview-pdf-amended", `${API}/api/sow/preview/sample.pdf?variant=amended`, "Sample amended PDF (v2)", FileText],
              ["sow-preview-email-signed", `${API}/api/sow/preview/email.html?kind=executed_client`, "Email: signed SOW", Mail],
              ["sow-preview-email-client-paper", `${API}/api/sow/preview/email.html?kind=client_paper_ack`, "Email: contract details", Mail],
            ].map(([id, href, label, Icon]) => (
              <a key={id} href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 bg-white px-3 py-2 text-xs font-medium text-stone-700 hover:border-[#1B4F8A] hover:text-[#1B4F8A]" data-testid={id}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </a>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap" data-testid="sow-preview-scenarios">
          {SCENARIOS.map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => { setKey(k); setEpoch((e) => e + 1); }}
              data-testid={`sow-preview-scenario-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                key === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-stone-200 hover:border-stone-300"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <Scenario key={`${key}-${epoch}`} scenario={scenario} />
      </div>
    </StaffShell>
  );
}
